import { Suspense, lazy, useState } from 'react';
import {
  usePublicDonationPage,
  isPublicDonationNotFound,
} from '@/hooks/queries/usePublicDonationPage';
import type { ChargeFeedback, DonationChargeResult } from '@/lib/publicDonationApi';
import { assertNever, type SimulateDonationInput } from '@signalvote/shared';
import { simulateDemoDonation } from '@/lib/demoDonationApi';
import { DonationForm } from './DonationForm';
import { DonationUnavailable } from './DonationUnavailable';
import { DonationThankYou } from './DonationThankYou';
import { DemoCardWidget } from './DemoCardWidget';

// Heavy Stripe SDK — isolated to the checkout screen and lazy-loaded (mirrors the
// EmbeddedOnboarding split). Tests mock this module path.
const EmbeddedDonationCheckout = lazy(() =>
  import('./EmbeddedDonationCheckout').then((m) => ({
    default: m.EmbeddedDonationCheckout,
  })),
);

type Phase =
  | { kind: 'form' }
  | { kind: 'checkout'; clientSecret: string; publishableKey: string; amount: number }
  // Demo mode (#319): the fake card widget stands in for Stripe's Embedded Checkout; Donate simulates.
  | { kind: 'demoCheckout'; fields: SimulateDonationInput; amount: number }
  | { kind: 'thankYou'; amount: number }
  // A charge-time 409 flips the whole page to unavailable (the load↔submit race).
  | { kind: 'unavailable' };

interface DonationPageProps {
  subsidiary: string;
  slug: string;
  /**
   * Embed mode: rendered inside a campaign's iframe (see the /embed/$slug route). Drops the
   * full-height tinted page canvas for a transparent, auto-height shell that sits cleanly on the
   * host site. The `.donation-embedded` scope also un-fixes the sticky action bar.
   */
  embedded?: boolean;
}

/**
 * Public donor donation page orchestrator + state machine.
 *
 * Branding: loading → skeleton; notFound → "not available"; ready===false →
 * unavailable; ready===true → the form. Phases: form → checkout → thankYou. A
 * charge 409 (NotReady) flips to unavailable — the account's charges toggled off
 * between the branding load and this submit; it is never a failed charge.
 */
export function DonationPage({ subsidiary, slug, embedded = false }: DonationPageProps) {
  const { data, isLoading } = usePublicDonationPage(subsidiary, slug);
  const [phase, setPhase] = useState<Phase>({ kind: 'form' });
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoErrored, setDemoErrored] = useState(false);

  // The outer canvas: the full tinted page normally, a transparent auto-height shell in an embed.
  const pageClass = embedded ? 'donation-embedded' : 'donation-page';

  if (isLoading || !data) {
    return (
      <main className={pageClass} aria-busy="true" aria-label="Loading donation page">
        <div className="donation-shell space-y-4 py-8">
          <div className="h-16 w-16 animate-pulse rounded-full bg-muted" />
          <div className="h-7 w-2/3 animate-pulse rounded bg-muted" />
          <div className="h-36 w-full animate-pulse rounded-lg bg-muted" />
          <div className="h-48 w-full animate-pulse rounded-lg bg-muted" />
        </div>
      </main>
    );
  }

  const page = data;
  if (isPublicDonationNotFound(page)) {
    return (
      <main className={pageClass}>
        <div className="donation-shell py-8 text-center">
          <p className="text-sm text-muted-foreground">This donation page isn&apos;t available.</p>
        </div>
      </main>
    );
  }

  // A demo campaign has no Stripe connected account, so `ready` is false — but the branded page must
  // still render its form and simulate the gift, so demo bypasses the unavailable gate.
  const unavailable = (page.ready === false && !page.isDemo) || phase.kind === 'unavailable';

  if (unavailable) {
    return (
      <main className={pageClass}>
        <div className="donation-shell py-8">
          <DonationUnavailable committeeName={page.committeeName} />
        </div>
      </main>
    );
  }

  /**
   * The charge-outcome router. Every `DonationChargeResult` variant is classified
   * here — the `assertNever` default makes a newly-added variant a compile error
   * rather than a silent dead-end (the donor clicking Donate and nothing happening).
   * Page-level transitions happen here; anything the donor should see inline on the
   * form is returned as `ChargeFeedback` for the form to render.
   */
  function handleChargeResult(result: DonationChargeResult, amount: number): ChargeFeedback {
    switch (result.status) {
      case 'created':
        setPhase({
          kind: 'checkout',
          clientSecret: result.clientSecret,
          publishableKey: result.publishableKey,
          amount,
        });
        return { kind: 'none' };
      case 'notReady':
        // The load↔submit race — charges toggled off between branding load and submit.
        setPhase({ kind: 'unavailable' });
        return { kind: 'none' };
      case 'blocked':
        // The last-mile limit flip: block-check passed, then the limit flipped before
        // the charge landed. Surface it through the form's existing block UI.
        return { kind: 'blocked', block: result.block };
      case 'rateLimited':
        return { kind: 'error', message: 'Too many attempts right now. Please wait a moment and try again.' };
      case 'invalid':
      case 'notFound':
      case 'error':
        // invalid/notFound are effectively unreachable from a correct form flow; error is a
        // genuine failure. All three are retryable from the donor's side — one generic message.
        return { kind: 'error', message: "We couldn't process your donation. Please try again." };
      default:
        return assertNever(result);
    }
  }

  if (phase.kind === 'checkout') {
    return (
      <main className={pageClass}>
        <div className="donation-shell py-6">
          <Suspense
            fallback={<div className="donation-panel p-6 text-sm text-muted-foreground">Preparing secure checkout…</div>}
          >
            <EmbeddedDonationCheckout
              clientSecret={phase.clientSecret}
              publishableKey={phase.publishableKey}
              onComplete={() => setPhase({ kind: 'thankYou', amount: phase.amount })}
            />
          </Suspense>
        </div>
      </main>
    );
  }

  if (phase.kind === 'demoCheckout') {
    const donate = async () => {
      setDemoSubmitting(true);
      setDemoErrored(false);
      const result = await simulateDemoDonation(phase.fields);
      setDemoSubmitting(false);
      if (result.status === 'ok') {
        setPhase({ kind: 'thankYou', amount: phase.amount });
      } else {
        setDemoErrored(true);
      }
    };
    return (
      <main className={pageClass}>
        <div className="donation-shell py-6">
          <DemoCardWidget
            committeeName={page.committeeName}
            amount={phase.amount}
            onDonate={donate}
            submitting={demoSubmitting}
            errored={demoErrored}
          />
        </div>
      </main>
    );
  }

  if (phase.kind === 'thankYou') {
    return (
      <main className={pageClass}>
        <div className="donation-shell py-8">
          <DonationThankYou committeeName={page.committeeName} amount={phase.amount} />
        </div>
      </main>
    );
  }

  return (
    <main className={pageClass}>
      <div className="donation-shell py-6 sm:py-9">
        <DonationForm
          subsidiary={subsidiary}
          slug={slug}
          page={page}
          onChargeResult={handleChargeResult}
          onDemoContinue={(fields, amount) => setPhase({ kind: 'demoCheckout', fields, amount })}
        />
      </div>
    </main>
  );
}
